export type Jurisdiction = 'SG' | 'MY' | string | null | undefined;

function parseAsUtcIfNoTz(dateStr: string): Date {
  // ISO 문자열에 타임존 정보가 없으면(예: 2026-03-17T05:13:00.000)
  // 브라우저가 로컬 타임으로 해석해버리므로, UTC로 간주하도록 'Z'를 붙여 파싱한다.
  // 이미 Z 또는 +08:00 같은 오프셋이 있으면 그대로 사용.
  const hasTz = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateStr.trim());
  const normalized = hasTz ? dateStr : `${dateStr}Z`;
  return new Date(normalized);
}

export function getTimeZoneInfo(jurisdiction: Jurisdiction): { timeZone: string; abbr: string } {
  const j = (jurisdiction || '').toString().toUpperCase();
  if (j === 'MY' || j === 'MALAYSIA') return { timeZone: 'Asia/Kuala_Lumpur', abbr: 'MYT' };
  // 기본값은 SG로 처리 (서비스 운영 기준: SGT, UTC+8)
  return { timeZone: 'Asia/Singapore', abbr: 'SGT' };
}

/** Unix ms → "13:58 SGT" (챗봇 메시지 시간, 24시간제) */
export function formatJurisdictionTimeFromMs(
  timestampMs: number,
  jurisdiction: Jurisdiction,
): string {
  const d = new Date(timestampMs);
  if (isNaN(d.getTime())) return '';
  const { timeZone, abbr } = getTimeZoneInfo(jurisdiction);
  const text = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${text} ${abbr}`;
}

export function formatJurisdictionDateTimeParts(
  dateStr: string | null | undefined,
  jurisdiction: Jurisdiction,
): { text: string; abbr: string } {
  if (!dateStr) return { text: '', abbr: getTimeZoneInfo(jurisdiction).abbr };
  const d = parseAsUtcIfNoTz(dateStr);
  if (isNaN(d.getTime())) return { text: '', abbr: getTimeZoneInfo(jurisdiction).abbr };

  const { timeZone, abbr } = getTimeZoneInfo(jurisdiction);
  const text = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);

  return { text, abbr };
}

/**
 * "Mar 17, 2026, 01:13 PM SGT" 형식으로 반환
 */
export function formatJurisdictionDateTime(
  dateStr: string | null | undefined,
  jurisdiction: Jurisdiction,
): string {
  if (!dateStr) return '';
  const { text, abbr } = formatJurisdictionDateTimeParts(dateStr, jurisdiction);
  if (!text) return '';
  return `${text} ${abbr}`;
}

export interface NotificationRelativeTimeLabels {
  justNow: () => string;
  minutesAgo: (count: number) => string;
  hoursAgo: (count: number) => string;
  daysAgo: (count: number) => string;
}

/**
 * 알림 상대 시간 — DB UTC(타임존 없음) 기준으로 경과 시간 계산 후 MYT/SGT 약어 표시
 */
export function formatNotificationRelativeTime(
  dateStr: string | null | undefined,
  jurisdiction: Jurisdiction,
  labels: NotificationRelativeTimeLabels,
): string {
  if (!dateStr) return '';
  const d = parseAsUtcIfNoTz(dateStr);
  if (isNaN(d.getTime())) return '';

  const { abbr } = getTimeZoneInfo(jurisdiction);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);

  let relative: string;
  if (mins < 1) relative = labels.justNow();
  else if (mins < 60) relative = labels.minutesAgo(mins);
  else {
    const hours = Math.floor(mins / 60);
    if (hours < 24) relative = labels.hoursAgo(hours);
    else relative = labels.daysAgo(Math.floor(hours / 24));
  }

  return `${relative} (${abbr})`;
}

